"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { MagniferLinear } from "solar-icon-set";
import { Button } from "../Button";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Tooltip } from "../Tooltip";
import { useUiLabels } from "../UiLabels/useUiLabels";

interface ResponsiveSearchInputProps {
  /** Whether the input is on mobile */
  isMobile: boolean;
  /** The children to render */
  children: ReactNode;
  /** Whether the input is open */
  isOpen?: boolean;
  /** Callback when the input is opened or closed */
  onOpenChange?: (isOpen: boolean) => void;
}

export function ResponsiveSearchInput({
  isMobile,
  children,
  isOpen: controlledIsOpen,
  onOpenChange,
}: ResponsiveSearchInputProps) {
  const uiLabels = useUiLabels();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleOpenChange = (open: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(open);
    }
    onOpenChange?.(open);
  };

  const wasMobileRef = useRef(isMobile);
  useEffect(() => {
    const wasMobile = wasMobileRef.current;
    wasMobileRef.current = isMobile;
    // Leaving compact mode unmounts the popover without closing it, so its retained open state
    // must be closed through the shared handler here — otherwise re-entering compact mode
    // reopens it with no user action, for both controlled and uncontrolled callers.
    if (wasMobile && !isMobile && isOpen) {
      handleOpenChange(false);
    }
  }, [isMobile]);

  if (!isMobile) return <>{children}</>;

  return (
    <Tooltip content={uiLabels.search.tooltip} color="foreground">
      <span className="inline-flex">
        <Popover isOpen={isOpen} onOpenChange={handleOpenChange} placement="bottom-end" offset={8}>
          <PopoverTrigger>
            <Button isIconOnly size="md" variant="light" aria-label={uiLabels.search.open}>
              <MagniferLinear className="text-default-400" size={16} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-2">{children}</PopoverContent>
        </Popover>
      </span>
    </Tooltip>
  );
}
